import React, { useEffect } from 'react';
import { Icon, Select, TextField } from '@gerdau/hefesto';
import { Tag } from '@gerdau/hefesto-mais-web';
import { useDispatch, useSelector } from 'react-redux';

import Table from '../../../components/Table';
import { HeaderTableWrapper } from './HeaderTableWrapper';
import { WithoutData } from './WithoutData';
import MessageChooseContract from '../../../components/MessageChooseContract';
import {
  ALLOWED_STATUS,
  ManifestationDataProps,
  StatusType,
  statusVariantMap
} from '../../../store/ducks/manifestations/types';
import { ApplicationState } from '../../../store';
import { FiltersProps, HandleDetailProps } from '..';
import { sendTagging } from '../../../store/ducks/tagging/actions';

import {
  MessageWithoutEmitter,
  TableCell,
  TableContainer,
  TableOrderNumberSelectable
} from './styles';

interface Props {
  onFilter: (column: keyof ManifestationDataProps, value: string) => void;
  onDetail: (data: HandleDetailProps) => void;
  data?: ManifestationDataProps[];
  filters: FiltersProps;
}

export const ManifestationTable = ({
  onFilter,
  onDetail,
  data,
  filters
}: Props) => {
  const dispatch = useDispatch();

  const { selectedCustomerId, loading, fetched } = useSelector(
    (state: ApplicationState) => state.manifestations
  );

  useEffect(() => {
    if (!!selectedCustomerId && data && data.length > 0) {
      dispatch(
        sendTagging({
          screenName: 'LG:Reclamações Gerais',
          event: 'view_reclamacoes_gerais'
        })
      );
    }
  }, [data, selectedCustomerId]);

  if (loading) {
    return null;
  }

  if (!selectedCustomerId || selectedCustomerId?.length !== 10 || !fetched) {
    return (
      <MessageChooseContract>
        <MessageWithoutEmitter>
          Selecione um emissor <br />
          para continuar.
        </MessageWithoutEmitter>
      </MessageChooseContract>
    );
  }

  if (
    (Object.values(filters).filter((filt) => filt).length === 0 &&
      data?.length === 0 &&
      fetched) ||
    !data
  ) {
    return <WithoutData />;
  }

  const getColumns = () => {
    return [
      {
        title: (
          <HeaderTableWrapper title="Número da reclamação">
            <TextField
              label={''}
              name="orderNumber"
              type={'text'}
              icon={<Icon option="Filter" />}
              iconPosition={'left'}
              value={filters.orderNumber}
              onChange={(e) => onFilter('orderNumber', e.target.value)}
            />
          </HeaderTableWrapper>
        ),
        dataIndex: 'orderNumber',
        width: 170,
        render: (text: string) => (
          <TableOrderNumberSelectable
            onClick={() => onDetail({ manifestationId: text })}
          >
            {text}
          </TableOrderNumberSelectable>
        )
      },
      {
        title: (
          <HeaderTableWrapper title="Data de criação">
            <TextField
              label={''}
              name="orderCreatedDate"
              type={'text'}
              icon={<Icon option="Filter" />}
              iconPosition={'left'}
              onChange={(e) => onFilter('orderCreatedDate', e.target.value)}
              value={filters.orderCreatedDate}
            />
          </HeaderTableWrapper>
        ),
        dataIndex: 'orderCreatedDate',
        width: 170,
        render: (text: string) => <>{text}</>
      },
      {
        title: (
          <HeaderTableWrapper title="Nome do contato">
            <TextField
              label={''}
              name="contactName"
              type={'text'}
              icon={<Icon option="Filter" />}
              iconPosition={'left'}
              onChange={(e) => onFilter('contactName', e.target.value)}
              value={filters.contactName}
            />
          </HeaderTableWrapper>
        ),
        dataIndex: 'contactName',
        render: (text: string) => <TableCell width={256}>{text}</TableCell>
      },
      {
        title: (
          <HeaderTableWrapper title="Ocorrência">
            <TextField
              label={''}
              name="occurrence"
              type={'text'}
              icon={<Icon option="Filter" />}
              iconPosition={'left'}
              onChange={(e) => onFilter('occurrence', e.target.value)}
              value={filters.occurrence}
            />
          </HeaderTableWrapper>
        ),
        dataIndex: 'occurrence',
        render: (text: string) => <TableCell width={256}>{text}</TableCell>
      },
      {
        title: (
          <HeaderTableWrapper title="Status">
            <Select
              label={''}
              name="status"
              placeholder="Selecione"
              options={['Todos', ...ALLOWED_STATUS].map((stts) => ({
                label: stts,
                value: stts
              }))}
              onSelect={(value: string | string[]) => {
                onFilter('status', value as string);
              }}
              value={filters.status}
            />
          </HeaderTableWrapper>
        ),
        dataIndex: 'status',
        width: 260,
        render: (text: string) => (
          <Tag variant={statusVariantMap[text as StatusType]}>{text}</Tag>
        )
      }
    ];
  };

  return (
    <TableContainer>
      <Table
        {...{
          columns: getColumns(),
          data,
          hasPagination: true,
          pageSizeInitial: 20,
          pageSizeOptions: [20, 30, 50]
        }}
      />
    </TableContainer>
  );
};
